import React, { createContext, useState, useContext, ReactNode, useEffect } from 'react';

interface AuthContextType {
  isAuthenticated: boolean;
  setIsAuthenticated: React.Dispatch<React.SetStateAction<boolean>>;
  start2FA: (username: string) => void;
  is2FAEnabled: boolean;
  setIs2FAEnabled: React.Dispatch<React.SetStateAction<boolean>>;
  setAuthToken: React.Dispatch<React.SetStateAction<string | null>>;
  authToken: string | null;
  setUsernameFor2FA: React.Dispatch<React.SetStateAction<string | null>>;
  usernameFor2FA: string | null;
  loginComplete: (token: string) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

// Client-side hard logout window. Set to 14 days to match the deliberate 14d
// session decision (#192) and AH2's backend Redis session-window bump to 14d.
// MUST stay in sync with the backend value — both sides are 14d. (14d in ms is
// under the setTimeout ~24.8d max, so no overflow.)
const TOKEN_EXPIRY_TIME = 14 * 24 * 60 * 60 * 1000; // 14 days in milliseconds

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [authToken, setAuthToken] = useState<string | null>(() => {
    const storedToken = localStorage.getItem('authToken');
    const expiryTime = localStorage.getItem('authTokenExpiry');

    if (storedToken && expiryTime && Number(expiryTime) > Date.now()) {
      console.log('Found Token which is valid');
      return storedToken;
    }
    localStorage.removeItem('authToken');
    localStorage.removeItem('authTokenExpiry');
    return null;
  });
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(!!authToken); // Set initial state based on authToken
  const [is2FAEnabled, setIs2FAEnabled] = useState(false);
  const [usernameFor2FA, setUsernameFor2FA] = useState<string | null>(null);

  useEffect(() => {
    // console.log('AuthToken changed:', authToken);
    if (authToken) {
      localStorage.setItem('authToken', authToken);
      localStorage.setItem('authTokenExpiry', String(Date.now() + TOKEN_EXPIRY_TIME));
      setIsAuthenticated(true);
      const expiryTimeout = Number(localStorage.getItem('authTokenExpiry')) - Date.now();
      const timeoutId = setTimeout(() => {
        logout();
      }, expiryTimeout);
      return () => clearTimeout(timeoutId);
    } else {
      localStorage.removeItem('authToken');
      localStorage.removeItem('authTokenExpiry');
      setIsAuthenticated(false);
    }
  }, [authToken]);

  const loginComplete = (token: string) => {
    setAuthToken(token);
  };

  const logout = () => {
    setAuthToken(null);
  };

  const start2FA = (username: string) => {
    console.log(`Initiating 2FA for user: ${username}`);
    setIs2FAEnabled(true);
    setUsernameFor2FA(username);
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        setIsAuthenticated,
        start2FA,
        is2FAEnabled,
        setIs2FAEnabled,
        setAuthToken,
        authToken,
        usernameFor2FA,
        setUsernameFor2FA,
        loginComplete,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};